'use client';

import type { FC } from 'react';
import { cn } from '../../utils/cn';
import type { CaseBattleReactionCountResponse } from '../../services/generated/Api';

interface ReactionCountsProps {
  reactions: CaseBattleReactionCountResponse[];
  size?: 's' | 'm';
  limit?: number;
  rest?: boolean;
  reserve?: boolean;
  className?: string;
}

const compact = (n: number): string => (n >= 1000 ? `${Math.floor(n / 100) / 10}k` : String(n));

export const ReactionCounts: FC<ReactionCountsProps> = ({ reactions, size = 'm', limit = 4, rest: showRest = true, reserve = false, className }) => {
  const shown = reactions.filter((r) => r.count > 0).slice(0, limit);
  const rest = reactions.slice(limit).reduce((sum, r) => sum + r.count, 0);
  const rowHeight = size === 'm' ? 'h-8' : 'h-6';
  if (shown.length === 0) return reserve ? <div className={cn(rowHeight, className)} aria-hidden /> : null;

  return (
    <div className={cn('flex items-center', rowHeight, size === 'm' ? 'gap-1.5' : 'gap-1', className)}>
      {shown.map((r) => (
        <span
          key={r.emoji}
          className={cn(
            'inline-flex shrink-0 items-center rounded-full bg-white/[0.06] font-bold text-label-primary',
            size === 'm' ? 'h-8 gap-1 pl-1.5 pr-2.5 text-[13px]' : 'h-6 gap-0.5 pl-1 pr-1.5 text-[11px]',
          )}
        >
          <span className={cn('leading-none', size === 'm' ? 'text-[18px]' : 'text-[14px]')}>{r.emoji}</span>
          <span className="tabular-nums">{compact(r.count)}</span>
        </span>
      ))}
      {showRest && rest > 0 && (
        <span className={cn('shrink-0 font-semibold text-label-tertiary tabular-nums', size === 'm' ? 'text-[13px]' : 'text-[11px]')}>
          +{compact(rest)}
        </span>
      )}
    </div>
  );
};
